import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="assistant-sheet" on the .sh-fab-drawer element.
//
// Adds bottom-sheet behaviour below 768px viewport width. Above 768px the
// existing side-anchored .sh-fab-drawer styles win and this controller is
// effectively idle (the drawer doesn't carry data-sheet-state attributes
// outside mobile).
//
// State is exposed via data-sheet-state on the drawer element:
//   "closed" — translateY(100%); the slide-assistant controller sets this
//              to "peek" via openDrawer() bridge below.
//   "peek"   — height: 50dvh; default open.
//   "full"   — height: 100dvh - safe-area-inset-top.
//
// Drag is implemented as a simple state machine — no momentum, no inertia.
// On touchend we snap to whichever state's threshold the y-offset crossed.
//
// Coordination with slide-assistant:
//   The existing slide-assistant#openDrawer toggles the drawer's hidden
//   attribute. We listen for the toggle via MutationObserver and set the
//   initial state to "peek" when the drawer becomes visible on mobile.
export default class extends Controller {
  connect() {
    this.mql = window.matchMedia("(max-width: 767px)")
    this.boundResize = this.handleViewportChange.bind(this)
    if (typeof this.mql.addEventListener === "function") {
      this.mql.addEventListener("change", this.boundResize)
    } else {
      this.mql.addListener(this.boundResize) // Safari < 14
    }

    this.observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === "hidden") this.handleHiddenChange()
      }
    })
    this.observer.observe(this.element, { attributes: true })

    this.handleHiddenChange()
  }

  disconnect() {
    if (typeof this.mql.removeEventListener === "function") {
      this.mql.removeEventListener("change", this.boundResize)
    } else {
      this.mql.removeListener(this.boundResize)
    }
    this.observer?.disconnect()
  }

  handleViewportChange() {
    if (!this.mql.matches) {
      // Crossed back to desktop while open — wipe the sheet state so
      // desktop CSS isn't fighting our inline transforms.
      this.element.removeAttribute("data-sheet-state")
      this.element.style.transform = ""
      this.element.style.height = ""
    } else if (!this.element.hidden) {
      this.setState("peek")
    }
  }

  handleHiddenChange() {
    if (!this.mql.matches) return
    if (this.element.hidden) {
      this.setState("closed")
    } else {
      this.setState("peek")
    }
  }

  setState(state) {
    if (this.element.dataset.sheetState === state) return
    this.element.dataset.sheetState = state
    this.element.style.transform = ""  // CSS handles transforms via [data-sheet-state]
  }

  // ─── Drag handling ──────────────────────────────────────────────────────
  dragStart(event) {
    if (!this.mql.matches) return
    const touch = event.touches?.[0]
    if (!touch) return
    this.dragStartY = touch.clientY
    this.dragStartHeight = this.element.getBoundingClientRect().height
    this.dragging = true
    // Pause the CSS transition during the drag for direct-manipulation feel.
    this.element.style.transition = "none"
  }

  dragMove(event) {
    if (!this.dragging) return
    const touch = event.touches?.[0]
    if (!touch) return
    event.preventDefault()
    const dy = touch.clientY - this.dragStartY
    const newHeight = Math.max(0, this.dragStartHeight - dy)
    this.element.style.height = `${newHeight}px`
  }

  dragEnd(event) {
    if (!this.dragging) return
    this.dragging = false
    this.element.style.transition = "" // restore CSS transition

    const finalHeight = this.element.getBoundingClientRect().height
    const vh = window.innerHeight
    const dy = this.dragStartHeight - finalHeight
    const fastSwipeDown = (event.changedTouches?.[0]?.clientY ?? 0) - this.dragStartY > 80 && Math.abs(dy) > 40

    // Clear inline height so [data-sheet-state] CSS rules win again.
    this.element.style.height = ""

    if (fastSwipeDown && finalHeight < vh * 0.55) {
      this.close()
      return
    }

    if (finalHeight > vh * 0.75) {
      this.setState("full")
    } else if (finalHeight > vh * 0.25) {
      this.setState("peek")
    } else {
      this.close()
    }
  }

  close() {
    // Defer to the existing slide-assistant#close so we share state — the
    // drawer's `hidden` attribute is the source of truth.
    const host = document.querySelector("[data-controller~='slide-assistant']")
    const closeBtn = host?.querySelector(".sh-fab-drawer-close")
    if (closeBtn) closeBtn.click()
    else this.element.hidden = true
  }
}
